import { apiFetchWithRetry } from "./apiFetch";

/**
 * Certificate Studio API client for school admins. Mirrors studentService /
 * staffService shape — modular service object, apiFetchWithRetry for the
 * Clerk auth + auto-refresh wrapper.
 *
 * Backing endpoints (all under /v1/admin/certificates, AuthGuard.SchoolAdmin):
 *   POST   /                  → issue
 *   GET    /?page=&pageSize=  → list (newest first, school-scoped)
 *   GET    /:id               → detail
 *   POST   /:id/revoke        → revoke
 *   POST   /:id/requeue       → re-enqueue PDF render
 *
 * The verify endpoint (GET /v1/pub/verify/cert/:code) is @Public() and used
 * by the Student app, not here.
 */
export const certificateService = {
  /**
   * Issue a new certificate. Server enqueues an async PDF render; the
   * response includes the cert id + verification code, but pdfUrl will be
   * null until the render finishes (poll list() to surface it).
   */
  issueCertificate: async (payload) => {
    try {
      const response = await apiFetchWithRetry(`/v1/admin/certificates`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to issue certificate");
      }
      return data;
    } catch (error) {
      console.error("Error issuing certificate:", error);
      throw error;
    }
  },

  /** Paged list of certs issued by the admin's school, newest first. */
  listCertificates: async ({ page = 1, pageSize = 25 } = {}) => {
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      const response = await apiFetchWithRetry(`/v1/admin/certificates?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to load certificates");
      }
      return data;
    } catch (error) {
      console.error("Error loading certificates:", error);
      throw error;
    }
  },

  /** Single cert detail (school-scoped). */
  getCertificate: async (id) => {
    try {
      const response = await apiFetchWithRetry(`/v1/admin/certificates/${encodeURIComponent(id)}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to load certificate");
      }
      return data;
    } catch (error) {
      console.error("Error loading certificate:", error);
      throw error;
    }
  },

  /**
   * Revoke a cert. Reason is optional but recommended — it shows on the
   * public verify page so external viewers know why.
   */
  revokeCertificate: async (id, reason = "") => {
    try {
      const response = await apiFetchWithRetry(
        `/v1/admin/certificates/${encodeURIComponent(id)}/revoke`,
        {
          method: "POST",
          body: JSON.stringify({ reason }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to revoke certificate");
      }
      return data;
    } catch (error) {
      console.error("Error revoking certificate:", error);
      throw error;
    }
  },

  /**
   * Render a watermarked preview PDF using the form's current values.
   * Returns a Blob — caller is responsible for createObjectURL + window.open.
   *
   * The response Content-Type is application/pdf, NOT JSON, so we don't
   * go through the standard "throw on !ok" path of the other methods —
   * we read the body as a blob if the request succeeded, or as text
   * (which the server returns for 4xx errors) to surface the error.
   */
  previewCertificate: async (payload) => {
    try {
      const response = await apiFetchWithRetry(`/v1/admin/certificates/preview`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        // Error responses come back as JSON; try to parse for a message.
        let message = "Preview failed";
        try {
          const errBody = await response.json();
          message = errBody.message || message;
        } catch {
          // Body wasn't JSON — keep generic message.
        }
        throw new Error(message);
      }
      return await response.blob();
    } catch (error) {
      console.error("Error rendering certificate preview:", error);
      throw error;
    }
  },

  /**
   * Live HTML preview — same branding/fonts as the PDF but rendered as HTML
   * (no Chromium), so it's near-instant and safe to call on every tweak.
   * Returns the HTML string for an iframe srcDoc.
   */
  previewCertificateHtml: async (payload) => {
    const response = await apiFetchWithRetry(`/v1/admin/certificates/preview/html`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      let message = "Preview failed";
      try {
        const errBody = await response.json();
        message = errBody.message || message;
      } catch {
        // keep generic message
      }
      throw new Error(message);
    }
    return await response.text();
  },

  /** Re-enqueue PDF render for a cert (use when initial render failed). */
  requeueRender: async (id) => {
    try {
      const response = await apiFetchWithRetry(
        `/v1/admin/certificates/${encodeURIComponent(id)}/requeue`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to requeue render");
      }
      return data;
    } catch (error) {
      console.error("Error requeueing certificate render:", error);
      throw error;
    }
  },
};
